import * as zod from 'zod';

import { Step } from '@sb/remote-control/types';
import { buildConditionalFromJSON } from '@sb/remote-control/util/conditionalsBuilder';
import convertJavaScriptExpressionToUser from '@sb/remote-control/util/expressions/convertJavaScriptExpressionToUser';
import { convertPythonExpression } from '@sb/remote-control/util/expressions/v2';
import { Expression } from '@sb/routine-runner';
import { roundToDecimalPlaces } from '@sb/utilities';

export namespace WaitStep {
  export const name = 'Wait';
  export const description =
    'Wait for a condition such as time, IO input, or an event';
  export const librarySection = Step.LibrarySection.Control;
  export const librarySort = '2';
  export const argumentKind = 'Wait';

  export type TimeInputKind = 'literal' | 'expression';

  export const Arguments = zod.object({
    argumentKind: zod.literal(argumentKind),
    // A Wait expresses exactly one of: a duration (milliseconds), a `condition`,
    // or a time `expression`. Each defaults to null so an author only supplies
    // the mode they're using and omits the others (rather than spelling out
    // `milliseconds: null` / `condition: null`). An all-null Wait is caught
    // downstream by toRoutineRunner / validation, not by this schema.
    milliseconds: zod.number().nullable().default(null),
    /**
     * Condition: will override `milliseconds` if non-null
     * The actual type is ConditionalBuilderState[][] but Firestore doesn't support nested
     * arrays, so we need to serialize/desarialize this property when using it.
     */
    condition: zod.string().nullable().default(null),
    timeInputKind: zod
      .enum(['literal', 'expression'])
      .optional()
      .default('literal'),
    expression: Expression.optional().nullable(),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  export function isMillisecondsArguments(
    args: Arguments,
  ): args is Arguments & { milliseconds: number } {
    return typeof args.milliseconds === 'number';
  }

  export function isExpressionArguments(
    args: Arguments,
  ): args is Arguments & { expression: Expression } {
    return args.timeInputKind === 'expression' && args.expression != null;
  }

  export function isConditionArguments(
    args: Arguments,
  ): args is Arguments & { condition: string } {
    return typeof args.condition === 'string';
  }

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    if (isConditionArguments(args)) {
      return {
        ...stepData,
        stepKind: 'Wait',
        args: { condition: buildConditionalFromJSON(args.condition) },
      };
    }

    if (isExpressionArguments(args)) {
      return {
        ...stepData,
        stepKind: 'Wait',
        args: { expression: args.expression },
      };
    }

    if (isMillisecondsArguments(args)) {
      return {
        ...stepData,
        stepKind: 'Wait',
        args: { milliseconds: args.milliseconds },
      };
    }

    throw new Error(
      'Either milliseconds, expression, or condition must be provided to a Wait step',
    );
  };

  export const getStepDescription: Step.GetStepDescription = ({
    stepConfiguration: { args },
    includeStepName,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      return null;
    }

    if (isExpressionArguments(args)) {
      const prefix = includeStepName ? 'Wait for ' : '';

      if (args.expression.kind === 'JavaScript') {
        const jsExprStr = convertJavaScriptExpressionToUser(
          args.expression.expression,
          { isForStepDescription: true },
        );

        return `${prefix}${jsExprStr} seconds`;
      }

      if (args.expression.kind === 'Python') {
        const { code: pyExprStr } =
          convertPythonExpression.withRemoteControlVariables.toUserDescription(
            args.expression.expression,
          );

        return `${prefix}${pyExprStr} seconds`;
      }

      return `${prefix}expression`;
    }

    if (args.milliseconds) {
      const timeS = roundToDecimalPlaces(args.milliseconds / 1_000, 2);

      return [
        includeStepName ? `Wait` : false,
        'for',
        timeS === 1 ? 'a second' : `${timeS} seconds`,
      ]
        .filter((line) => line !== false)
        .join(' ');
    }

    return null;
  };
}

WaitStep satisfies Step.StepKindInfo;
